import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { isAllClear, resolveI18nText, todayDigest, type Locale } from '@blauturquesa/shared';
import { canUseModule } from '@blauturquesa/shared/roles-lite';
import { Screen, type SubmenuItem } from '../../shell/Screen';
import { EmptyState } from '../../components/EmptyState';
import { Loading } from '../../components/Loading';
import { Icon } from '../../components/Icon';
import { RequireProfile } from '../../shell/RequireProfile';
import { useAuth } from '../auth/AuthProvider';
import { watchOpenInquiries, type InquiryRow } from '../inquiries/live';
import { listOpportunities, type OpportunityRow } from '../opportunities/api';

const STAFF = ['agent', 'superadmin'] as const;
type Section = 'all' | 'answer' | 'quotes' | 'expiring' | 'opportunities';
/** Oportunitats que demanen seguiment: les que esperen algú o estan en negociació. */
const FOLLOW_UP = ['pending', 'waitingOwner', 'negotiating'] as const;

/**
 * Tauler de l'agent (§10.4): el que li toca fer, ajuntat des de Missatges i Oportunitats. Cada línia
 * porta a la conversa o a l'oportunitat. Quan hi hagi reserves, s'hi afegiran entrades i sortides.
 */
export function TodayPage() {
  const { t } = useTranslation(['shell', 'inquiries', 'opportunities']);
  const state = useAuth();
  const me = state.status === 'signedIn' && (state.activeRole === 'agent' || state.activeRole === 'superadmin') ? state : null;
  const [section, setSection] = useState<Section>('all');
  const [rows, setRows] = useState<InquiryRow[] | null>(null);
  const [opps, setOpps] = useState<OpportunityRow[]>([]);
  const [failed, setFailed] = useState(false);
  const hasOpps = me !== null && canUseModule(me.activeRole, me.modules, 'opportunities');

  useEffect(() => {
    if (!me) return;
    return watchOpenInquiries(me.user.uid, me.activeRole, setRows, () => setFailed(true));
  }, [me?.user.uid, me?.activeRole]);
  useEffect(() => {
    if (!me || !hasOpps) return;
    listOpportunities(me.user.uid, me.activeRole === 'superadmin')
      .then((r) => setOpps(r.rows.filter((o) => (FOLLOW_UP as readonly string[]).includes(o.status))))
      .catch(() => setOpps([]));
  }, [me?.user.uid, me?.activeRole, hasOpps]);

  const digest = useMemo(() => (rows && me ? todayDigest(rows, me.user.uid, new Date().toISOString()) : null), [rows, me?.user.uid]);
  const name = state.status === 'signedIn' ? (state.user.displayName ?? '').split(' ')[0] : '';

  const submenu: SubmenuItem[] = digest ? [
    { id: 'all', label: t('today.sections.all'), active: section === 'all', onSelect: () => setSection('all') },
    { id: 'answer', label: t('today.sections.answer'), count: digest.toAnswer.length, active: section === 'answer', onSelect: () => setSection('answer') },
    { id: 'quotes', label: t('today.sections.quotes'), count: digest.quotesToConfirm.length + digest.quotesWaiting.length, active: section === 'quotes', onSelect: () => setSection('quotes') },
    { id: 'expiring', label: t('today.sections.expiring'), count: digest.expiringSoon.length, active: section === 'expiring', onSelect: () => setSection('expiring') },
    ...(hasOpps ? [{ id: 'opportunities', label: t('today.sections.opportunities'), count: opps.length, active: section === 'opportunities', onSelect: () => setSection('opportunities') }] : []),
  ] : [];

  return (
    <Screen title={t('tabs.today')} submenu={submenu} submenuLabel={t('tabs.today')}>
      <RequireProfile profiles={STAFF}>
        <h2 className="title-underline greeting">{name ? t('today.hello', { name }) : t('today.helloAnon')}</h2>
        {failed ? <EmptyState icon="globe" title={t('inquiries:errors.loadTitle')}>{t('inquiries:errors.loadBody')}</EmptyState>
          : !digest ? <Loading />
          : <Digest digest={digest} opps={opps} section={section} />}
      </RequireProfile>
    </Screen>
  );
}

function Digest({ digest, opps, section }: { digest: ReturnType<typeof todayDigest<InquiryRow>>; opps: OpportunityRow[]; section: Section }) {
  const { t, i18n } = useTranslation(['shell', 'opportunities']);
  const locale = i18n.language as Locale;
  const eur = new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' });
  const time = new Intl.DateTimeFormat(locale, { weekday: 'short', hour: '2-digit', minute: '2-digit' });
  const day = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' });
  const house = (q: InquiryRow) => resolveI18nText(q.propertyTitle, locale, q.propertySourceLocale as Locale).text || q.slug;
  const dates = (q: InquiryRow) => `${day.format(new Date(`${q.from}T00:00:00Z`))} → ${day.format(new Date(`${q.to}T00:00:00Z`))}`;
  const show = (s: Section) => section === 'all' || section === s;
  const empty = isAllClear(digest) && opps.length === 0;

  const item = (key: string, to: string, title: string, sub: string, note?: string, urgent?: boolean) => (
    <li key={key}>
      <Link to={to} className={`today-row glass-card${urgent ? ' is-urgent' : ''}`}>
        <span className="grow">
          <strong className="block">{title}</strong>
          <span className="muted small block">{sub}</span>
          {note && <span className="small block">{note}</span>}
        </span>
        <Icon name="chevronRight" size={18} />
      </Link>
    </li>
  );
  const block = (s: Section, title: string, n: number, children: React.ReactNode) => show(s) && n > 0 && (
    <section className="today-block">
      <p className="menu-section">{title} · {n}</p>
      <ul className="today-list">{children}</ul>
    </section>
  );

  if (empty) return <EmptyState icon="check" title={t('today.allClear')}>{t('today.allClearBody')}</EmptyState>;
  const nothingHere = section !== 'all' && (
    (section === 'answer' && digest.toAnswer.length === 0) || (section === 'quotes' && digest.quotesToConfirm.length + digest.quotesWaiting.length === 0)
    || (section === 'expiring' && digest.expiringSoon.length === 0) || (section === 'opportunities' && opps.length === 0));

  return (
    <div className="stack">
      {nothingHere && <p className="muted">{t('today.nothingHere')}</p>}
      {block('answer', t('today.sections.answer'), digest.toAnswer.length,
        digest.toAnswer.map((q) => item(`a${q.id}`, `/missatges/${q.id}`, q.client.name, `${house(q)} · ${dates(q)}`, q.lastMessage?.text)))}
      {block('quotes', t('today.sections.quotes'), digest.quotesToConfirm.length + digest.quotesWaiting.length, <>
        {digest.quotesToConfirm.map((q) => item(`c${q.id}`, `/missatges/${q.id}`, q.client.name, `${house(q)} · ${dates(q)}`, t('today.toConfirm', { total: eur.format((q.offer?.quote.totalCents ?? 0) / 100) }), true))}
        {digest.quotesWaiting.map((q) => item(`w${q.id}`, `/missatges/${q.id}`, q.client.name, `${house(q)} · ${dates(q)}`, t('today.waiting', { total: eur.format((q.offer?.quote.totalCents ?? 0) / 100) })))}
      </>)}
      {block('expiring', t('today.sections.expiring'), digest.expiringSoon.length,
        digest.expiringSoon.map((e) => item(`e${e.kind}${e.row.id}`, `/missatges/${e.row.id}`, e.row.client.name, `${house(e.row)} · ${dates(e.row)}`,
          t(e.kind === 'hold' ? 'today.holdExpires' : 'today.quoteExpires', { time: time.format(new Date(e.at)) }), true)))}
      {block('opportunities', t('today.sections.opportunities'), opps.length,
        opps.map((o) => {
          const since = o.statusHistory?.[o.statusHistory.length - 1]?.at;
          const days = since ? Math.floor((Date.now() - since) / 86_400_000) : null;
          return item(`o${o.id}`, `/oportunitats/${o.id}`, o.title, t(`opportunities:status.${o.status}`), days !== null ? t('today.since', { count: days }) : undefined);
        }))}
    </div>
  );
}
